'use client';

import { useState, useEffect } from 'react';
import { type RegionsStats } from '@/types/RegionsStats';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  type TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { formatSeconds } from '@/utils/time';
import { type LocationData } from '@/types/TrendLineData';

import { Card, CardContent } from '@/components/ui/card';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from '@/components/ui/chart';
import CardHeaderGrid from '@/components/ui/CardHeaderGrid';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// Data type interfaces
interface RegionsChartData {
  locationData: LocationData[];
  barData: BarData[];
}

interface BarData {
  region: string;
  [id: string]: number | string; // locationId-time for service time at region
}

const chartConfig = {
  region: {
    label: 'Region',
  },
  time: {
    label: 'Service Time',
  },
} satisfies ChartConfig;

const orderMap: Record<string, number> = {
  pre: 1,
  order: 2,
  payment: 3,
  leave: 4,
  pull: 5,
};

// Data type converters
function orderRegionNames(data: RegionsChartData): RegionsChartData {
  // Hardcoded for Miguel's, with wildcards

  const getOrder = (region: string): number => {
    for (const key in orderMap) {
      if (region.toLowerCase().startsWith(key.toLowerCase())) {
        return orderMap[key];
      }
    }
    return Infinity; // Default if no match is found
  };

  data.barData.sort((a, b) => {
    return getOrder(a.region) - getOrder(b.region);
  });

  return data;
}

export function RegionsGraph({
  regionsData,
  colorMap,
  averageEnabled
}: {
  regionsData: RegionsStats[] | null;
  colorMap: Record<string, string>;
  averageEnabled: boolean;
}): JSX.Element {
  const [chartData, setChartData] = useState<RegionsChartData | null>(null);
  const [regionNames, setRegionNames] = useState<string[]>();
  const [flatData, setFlatData] = useState<any[]>();
  const [expand, setExpand] = useState<boolean>(false);

  const createFlatBarData = (
    locationData: LocationData[],
    names: string[]
  ): any[] => {
    const flatData: any[] = [];
    locationData.forEach((loc) => {
      names.forEach((rName) => {
        flatData.push({
          dataKey: `${rName}`,
          key: `${loc.locationName}`,
          name: `${loc.locationName}`,
          fill: loc.color,
        });
      });
    });
    return flatData;
  };

  // Convert RegionsStats[] to RegionsChartData[]
  function convertToRegionsChartData(
    rawData: RegionsStats[] | null
  ): RegionsChartData {
    const newData: RegionsChartData = {
      locationData: [],
      barData: [],
    };
    const dateMap = new Map();

    if (rawData) {
      // Add location data
      newData.locationData = rawData.map((data) => ({
        locationName: data.locationName,
        locationId: data.locationId,
        color: colorMap[data.locationId],
      }));

      // Add bar data for this location
      rawData.forEach((loc) => {
        loc.regions.forEach((r) => {
        if (r.timeSpent !== 0) {
          if (r.key in orderMap) {
              const value = orderMap[r.key];
              orderMap[r.displayName] = value;
          }
          const serviceTimeKey = `${loc.locationName}`;
          // regions[region.key] = region.displayName;
          if (!dateMap.has(r.displayName)) {
            dateMap.set(r.displayName, {
              [serviceTimeKey]: r.timeSpent,
            });
          } else {
            const existingData = dateMap.get(r.displayName);
            existingData[serviceTimeKey] = r.timeSpent;
          }
        }
        });
      });

      if (averageEnabled && rawData.length > 1) {
        newData.locationData.push({
          locationName: 'Average (selected)',
          locationId: 'average_selected',
          color: '#888888', // darker gray

        });
        const allEntry = {
              locationId: 'average_selected',
              locationName: 'Average (selected)',
              regions: (() => {
                // Collect all unique region keys across all locations
                const allRegionKeys = Array.from(
                  new Set(
                    rawData.flatMap((r) =>
                      r.regions.map((region) => region.key)
                    )
                  )
                );

                return allRegionKeys.map((regionKey) => {
                  // Get all region objects for this regionKey
                  const regionEntries = rawData
                    .map((r) =>
                      r.regions.find((region) => region.key === regionKey)
                    )
                    .filter(Boolean);

                  // Calculate average timeSpent for this regionKey
                    const validEntries = regionEntries.filter(
                    (region) => region?.timeSpent != null && region.timeSpent !== 0
                    );
                    const avgValue =
                    validEntries.length > 0
                      ? validEntries.reduce(
                        (sum, region) => sum + (region?.timeSpent ?? 0),
                        0
                      ) / validEntries.length
                      : 0;

                  // Use the first region object as a template
                  const templateRegion = regionEntries[0];

                  return {
                    ...templateRegion,
                    value: avgValue,
                    timeSpent: avgValue,
                    locationId: 'average_selected',
                    locationName: 'Average (selected)',
                    key: regionKey, // Ensure key is always a string
                  };
                });
              })(),
            };

        allEntry.regions.forEach((r) => {
          if (r.key in orderMap) {
            const value = orderMap[r.key];
            orderMap[r.displayName] = value;
          }
          const serviceTimeKey = `${allEntry.locationName}`;
          // regions[region.key] = region.displayName;
          if (!dateMap.has(r.displayName)) {
            dateMap.set(r.displayName, {
              [serviceTimeKey]: r.timeSpent,
            });
          } else {
            const existingData = dateMap.get(r.displayName);
            existingData[serviceTimeKey] = r.timeSpent;
          }
        });
      }
    }

    newData.barData = Array.from(dateMap.entries()).map(([region, data]) => ({
      region,
      ...data,
    }));

    return orderRegionNames(newData);
  }

  const CustomTooltip: React.FC<TooltipProps<any, any>> = ({
    active,
    payload,
  }) => {
    if (active && payload?.length) {
      const sortedPayload = [...payload].sort(
        (b, a) => (b.payload[a.dataKey] ?? 0) - (a.payload[b.dataKey] ?? 0)
      );
      return (
        <div
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            padding: '5px',
            borderRadius: '4px',
            fontSize: 'small',
          }}
        >
          {sortedPayload.map((pl) => (
            <div
              key={pl.dataKey}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <div
                style={{
                  width: '1em',
                  height: '1em',
                  backgroundColor: pl.color,
                  marginRight: '0.5em',
                }}
              />
              <p>{`${pl.name} ${pl.payload.region} - Time: ${formatSeconds(
                pl.payload[pl?.dataKey || 0]
              )}`}</p>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };
  useEffect(() => {
    // Create chart data
    const newChartData = convertToRegionsChartData(regionsData);
    // Set chart data
    setChartData(newChartData);
    // Set region names
    const names = newChartData.barData.map((r) => r.region);
    setRegionNames(names);
    // Set flat version of bar data
    setFlatData(createFlatBarData(newChartData.locationData, names));
  }, [regionsData, averageEnabled]);

  if (!chartData || !regionNames) {
    return <div>{null}</div>;
  }

  return (
    <div
      className={`${
        expand ? 'w-100' : 'w-50'
      } transition-all duration-300 ease-in-out`}
    >
      <Card>
        <CardHeaderGrid
          title={'Stations'}
          expand={expand}
          setExpand={setExpand}
        ></CardHeaderGrid>
        <CardContent>
          {((chartData?.barData?.length) !== 0)
? (
            <ChartContainer config={chartConfig}>
              <BarChart
                accessibilityLayer
                data={chartData.barData}
                barSize={100}
              >
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="region"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                />
                <YAxis
                  label={{
                    value: 'Time Spent',
                    angle: -90,
                    position: 'insideLeft',
                  }}
                  tickFormatter={(value) => formatSeconds(value)}
                />
                <ChartTooltip cursor={false} content={<CustomTooltip />} />
                {[
                  ...new Map(
                    flatData?.map((item) => [item.key, item])
                  ).values(),
                ].map((data) => (
                  <Bar
                    dataKey={data.key}
                    key={data.dayPart}
                    fill={data.fill}
                    opacity={data.fill === '#888888' ? 0.2 : 1}
                    radius={4}
                    name={data.name}
                  />
                ))}
              </BarChart>
            </ChartContainer>
          )
: (
              <>
              <LoadingSpinner/>
              </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
